import {
  LoggerUtils,
  Mapper,
  NilUtils,
  ReadOnly
  } from 'module/Common';
import { MzImageResolution } from './MzImageResolution';
import { MzImageResolutionMap } from './MzImageResolutionMap';
import { MzImageResolutionName, MzImageResolutionNameOrdered } from './MzImageResolutionName';
import { Size } from 'module/Model/Size';

export type MzImage = ReadOnly<MzImage.Shape>;

const devicePixelRatio = window.devicePixelRatio || 1;

export namespace MzImage {
  export interface Shape {
    id: number;
    title: string;
    alt: string;
    width: number;
    height: number;
    resolutions: MzImageResolutionMap;
    border: false | { width: number; color: string };
  }

  const create = (input: MzImage) => input;

  export const mapper = Mapper.mapSchema(
    {
      id: Mapper.num,
      title: Mapper.str,
      alt: Mapper.str,
      width: Mapper.num,
      height: Mapper.num,
      resolutions: MzImageResolutionMap.mapper,
      border: Mapper.bool,
      border_color: Mapper.nullable(Mapper.str),
      border_width: Mapper.nullable(Mapper.stringInt),
    },
    ({ id, title, alt, width, height, resolutions, border, border_color, border_width }) => {
      const borderObj =
        border && border_color !== null && border_width !== null ? { color: border_color, width: border_width } : false;
      return create({ id, title, alt, width, height, resolutions, border: borderObj });
    }
  );

  const errorImage: MzImage = Mapper.exec(
    Mapper.errorWrapper(err => {
      throw new Error(`Invalid data for errorImage`);
    }, mapper),
    {
      id: 0,
      title: 'missing image',
      alt: 'missing image',
      width: 1000,
      height: 1000,
      resolutions: {
        xxl: [1600, 1600, '#'],
        xl: [1400, 1400, '#'],
        l: [1200, 1200, '#'],
        m: [1000, 1000, '#'],
        s: [800, 800, '#'],
        xs: [600, 600, '#'],
        xxs: [400, 400, '#'],
      },
      border: false,
      border_color: null,
      border_width: null,
    }
  );

  export const mapperSafe = Mapper.catchError(e => {
    LoggerUtils.logError(e);
    return errorImage;
  }, mapper);

  export const getResolutions = (model: MzImage): MzImageResolutionMap => model.resolutions;
  export const resolveResolution = (model: MzImage, size: Size): MzImageResolution => {
    const scaledSize = Size.scale(size, devicePixelRatio);
    const resolutions = getResolutions(model);
    const resolvedRes: MzImageResolutionName = MzImageResolutionNameOrdered.reduce<MzImageResolutionName>(
      (acc, resName) => {
        const nextSize = MzImageResolution.getSize(resolutions[resName]);
        if (Size.fitInside(scaledSize, nextSize)) {
          return resName;
        }
        return acc;
      },
      MzImageResolutionName.Xxl
    );

    return resolutions[resolvedRes];
  };
  export const getWidth = (model: MzImage): number => model.width;
  export const getHeight = (model: MzImage): number => model.height;
  export const getSize = (model: MzImage): Size => Size.create(getWidth(model), getHeight(model));
  export const getRatio = (model: MzImage): number => getWidth(model) / getHeight(model);
  export const getAlt = (model: MzImage): string => model.alt;

  export const getUrlOrNull = (model: MzImage | null, resolution: MzImageResolutionName): string | null => {
    return NilUtils.bubbleNull<MzImage, MzImageResolution, string>(
      v => getResolutions(v)[resolution],
      v => MzImageResolution.getUrl(v)
    )(model);
  };
}



// WEBPACK FOOTER //
// ./src/module/Model/MzImage/MzImage.ts